import React, { useState } from 'react';
import { RefreshCw, Package, Droplet, CheckCircle, MapPin, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ModalCompletarEntrega from './ModalCompletarEntrega';

const EntregasEnCamino = ({ entregas, isLoading, onCompletar, onRefresh }) => {
  const [entregaSeleccionada, setEntregaSeleccionada] = useState(null);
  const [showModalCompletar, setShowModalCompletar] = useState(false);

  const handleCompletarClick = (entrega) => {
    setEntregaSeleccionada(entrega);
    setShowModalCompletar(true);
  };

  const handleConfirmarEntrega = async (datosEntrega) => {
    await onCompletar(datosEntrega);
    setShowModalCompletar(false);
    setEntregaSeleccionada(null);
    if (onRefresh) {
      onRefresh();
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-32 bg-gray-200 rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (entregas.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">No hay entregas en camino</p>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {entregas.map((entrega) => {
        const TipoIcon = entrega.tipo === 'lenceria' ? Package : Droplet;

        return (
          <Card
            key={entrega._id}
            className="hover:shadow-md transition-shadow border-l-4 border-l-yellow-500"
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <TipoIcon className={`h-5 w-5 ${
                    entrega.tipo === 'lenceria' ? 'text-blue-600' : 'text-purple-600'
                  }`} />
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {entrega.edificio?.nombre}
                    </h3>
                    {entrega.hospedaje?.nombre && (
                      <p className="text-xs text-gray-500">
                        {entrega.hospedaje.nombre}
                      </p>
                    )}
                  </div>
                </div>
                <Badge className="bg-yellow-100 text-yellow-700">
                  En Camino
                </Badge>
              </div>

              {/* Dirección */}
              <div className="flex items-start gap-2 mb-3 text-sm text-gray-600 bg-gray-50 p-2 rounded">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-500" />
                <span className="line-clamp-2">
                  {entrega.edificio?.direccion}, {entrega.edificio?.ciudad}
                </span>
              </div>

              {/* Información */}
              <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                <div className="bg-gray-50 rounded p-2 text-center">
                  <div className="text-gray-500">Items</div>
                  <div className="font-semibold text-gray-900">
                    {entrega.total_items || 0}
                  </div>
                </div>
                <div className="bg-gray-50 rounded p-2 text-center">
                  <div className="text-gray-500">Peso</div>
                  <div className="font-semibold text-gray-900">
                    {((entrega.peso_total || 0) / 1000).toFixed(1)} kg
                  </div>
                </div>
                <div className="bg-gray-50 rounded p-2 text-center">
                  <div className="text-gray-500">Tipo</div>
                  <div className="font-semibold text-gray-900 capitalize">
                    {entrega.tipo || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Tiempo en camino */}
              {entrega.fecha_despacho && (
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                  <Clock className="h-3 w-3" />
                  <span>
                    En camino desde: {new Date(entrega.fecha_despacho).toLocaleTimeString('es-ES', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              )}

              {/* Botón de completar */}
              <Button
                onClick={() => handleCompletarClick(entrega)}
                className="w-full bg-green-600 hover:bg-green-700"
                size="lg"
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                Completar Entrega
              </Button>
            </CardContent>
          </Card>
        );
      })}

      {/* Modal de Completar Entrega */}
      <ModalCompletarEntrega
        entrega={entregaSeleccionada}
        isOpen={showModalCompletar}
        onClose={() => {
          setShowModalCompletar(false);
          setEntregaSeleccionada(null);
        }}
        onConfirm={handleConfirmarEntrega}
      />
    </div>
  );
};

export default EntregasEnCamino;
